import { CustomEditor } from '@nucleus/lib-slate';
import { withLists } from '@nucleus/ncu-slate';
import React from 'react';
import { Descendant, Editor } from 'slate';
import { Editable, RenderElementProps, RenderLeafProps } from 'slate-react';
import styled from 'styled-components';
import { cornerStyleInterpolationFactory } from '../lib/cornerStyle';
import { BodyElement } from '../slate/elements/BodyElement';
import { LinkElement } from '../slate/elements/LinkElement';
import { ParagraphElement } from '../slate/elements/ParagraphElement';
import { TextAreaElement } from '../slate/elements/TextAreaElement';
import { TitleElement } from '../slate/elements/TitleElement';
import { withForcedLayout } from '../slate/plugins/withForcedLayout';
import { withInsertBreak } from '../slate/plugins/withInsertBreak';
import { withToPlainText } from '../slate/plugins/withToPlainText';

declare module '@nucleus/sermon-theme-elements' {
  interface SermonThemeElementsTheme {
    NotesEditor?: {
      background?: string;
      border?: string;
      color?: string;
      focus?: {
        border?: string;
        boxShadow?: string;
      };
      padding?: string;
      elements?: NotesEditorElements;
    };
  }
}

const defaultValue: Descendant[] = [
  {
    type: 'title',
    placeholder: 'Add a title…?',
    children: [{ text: '' }],
  },
  {
    type: 'body',
    placeholder:
      'You can type as much or as little as you want. And when you’re done, you can email the notes to yourself, download them, or share.',
    children: [
      {
        type: 'paragraph',
        children: [{ text: '' }],
      },
    ],
  },
];

type Props = {
  initialValue?: Descendant[];
  onValueChange: (value: Descendant[]) => void;
} & Omit<React.TextareaHTMLAttributes<HTMLDivElement>, 'onChange'>;

export const NotesEditor = React.forwardRef<Editor, Props>(
  ({ initialValue = defaultValue, onValueChange, ...props }, ref): JSX.Element => {
    return (
      <CustomEditor
        initialValue={initialValue}
        onValueChange={onValueChange}
        plugins={[withForcedLayout, withInsertBreak, withLists, withToPlainText]}
        ref={ref}
      >
        <StyledEditable renderElement={renderElement} renderLeaf={renderLeaf} {...props} />
      </CustomEditor>
    );
  }
);

NotesEditor.displayName = 'NotesEditor';

const StyledEditable = styled(Editable)`
  background: ${({ theme }) => theme._sermonThemeElements.NotesEditor?.background};
  color: ${({ theme }) => theme._sermonThemeElements.NotesEditor?.color};
  border: ${({ theme }) => theme._sermonThemeElements.NotesEditor?.border};
  border-radius: ${cornerStyleInterpolationFactory({
    square: '0px',
    rounded: '24px',
    pill: '25px',
  })};
  padding: ${({ theme }) => theme._sermonThemeElements.NotesEditor?.padding};
  outline: none;
  min-height: 250px;
  max-height: 60vh;
  overflow: scroll;
  transition:
    border 0.2s ease,
    box-shadow 0.2s ease;

  :focus-within {
    border: ${({ theme }) => theme._sermonThemeElements.NotesEditor?.focus?.border};
    box-shadow: ${({ theme }) => theme._sermonThemeElements.NotesEditor?.focus?.boxShadow};
  }
`;

const renderElement = (props: RenderElementProps): JSX.Element => {
  switch (props.element.type) {
    case 'title':
      return <TitleElement {...props} element={props.element} />;
    case 'body':
      return <BodyElement {...props} element={props.element} />;
    case 'textarea':
      return <TextAreaElement {...props} element={props.element} />;
    case 'link':
      return <LinkElement {...props} element={props.element} />;
    case 'bulleted-list':
      return <ParagraphElement as="ul" {...props} element={props.element} />;
    case 'numbered-list':
      return <ParagraphElement as="ol" {...props} element={props.element} />;
    case 'list-item':
      return <ParagraphElement as="li" {...props} element={props.element} />;
    case 'paragraph':
    default:
      return <ParagraphElement {...props} element={props.element} />;
  }
};

const renderLeaf = ({ attributes, children, leaf }: RenderLeafProps): JSX.Element => {
  let leafElement = <span {...attributes}>{children}</span>;

  if (leaf.bold) {
    leafElement = <strong {...attributes}>{leafElement}</strong>;
  }

  if (leaf.code) {
    leafElement = <code {...attributes}>{leafElement}</code>;
  }

  if (leaf.italic) {
    leafElement = <em {...attributes}>{leafElement}</em>;
  }

  if (leaf.underline) {
    leafElement = <u {...attributes}>{leafElement}</u>;
  }

  if (leaf.highlight) {
    leafElement = <mark {...attributes}>{leafElement}</mark>;
  }

  if (leaf.strike) {
    leafElement = <s {...attributes}>{leafElement}</s>;
  }

  return leafElement;
};
